import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";

/**
 * Textarea with simple @mention autocomplete. Calls /api/mentions/lookup
 * whenever the cursor sits inside a "@<token>" fragment.
 */
export default function MentionTextarea({ value, onChange, placeholder, rows = 3, "data-testid": testId, disabled = false }) {
  const ref = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [cursorIdx, setCursorIdx] = useState(null);

  useEffect(() => {
    if (cursorIdx == null) return;
    const before = value.slice(0, cursorIdx);
    const match = before.match(/@([A-Za-z0-9_]{1,30})$/);
    if (!match) {
      setSuggestions([]);
      return;
    }
    const q = match[1];
    if (!q) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    api.get(`/mentions/lookup`, { params: { q } }).then((r) => {
      if (!cancelled) setSuggestions(r.data.items || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [value, cursorIdx]);

  const handleChange = (e) => {
    onChange(e.target.value);
    setCursorIdx(e.target.selectionStart);
  };
  const handleKeyUp = (e) => {
    setCursorIdx(e.target.selectionStart);
  };
  const pickMention = (item) => {
    if (cursorIdx == null) return;
    const before = value.slice(0, cursorIdx).replace(/@([A-Za-z0-9_]*)$/, `@${item.handle} `);
    const after = value.slice(cursorIdx);
    const next = before + after;
    onChange(next);
    setSuggestions([]);
    setTimeout(() => ref.current?.focus(), 0);
  };

  return (
    <div className="relative">
      <textarea
        ref={ref}
        value={value}
        onChange={handleChange}
        onKeyUp={handleKeyUp}
        onClick={handleKeyUp}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        className="csc-input min-h-[80px] resize-none csc-break-anywhere"
        data-testid={testId}
      />
      {suggestions.length > 0 && (
        <div className="absolute z-20 left-0 right-0 mt-1 csc-card max-h-56 overflow-auto" data-testid="mention-suggestions">
          {suggestions.map((s) => (
            <button
              key={`${s.type}-${s.id}`}
              type="button"
              onClick={() => pickMention(s)}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-[#00aaff]/10 border-b border-[rgba(0,170,255,0.18)] last:border-none text-left"
              data-testid={`mention-${s.handle}`}
            >
              <span className={`font-mono-tech text-[10px] uppercase tracking-[0.2em] ${s.type === "org" ? "text-[#39ff14]" : "text-[#00aaff]"}`}>{s.type}</span>
              <span className="text-white">@{s.handle}</span>
              <span className="text-[#94a3b8] text-xs truncate">{s.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
